/**
 * Messenger Widget Component
 *
 * Floating chat panel with multiple modes:
 * - Mini: 360x480px conversation list or quick chat
 * - Expanded: 600x700px larger view with more features
 * - Full: Overlay covering entire viewport
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { messengerAPI } from '../services/messenger-api';
import { eventBus, Events } from '../services/events';
import type { Conversation, Message, MessengerWidgetMode } from '../types/messenger';

// Import chat component
import './messenger-chat';

@customElement('moya-messenger-widget')
export class MoyaMessengerWidget extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: fixed;
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    /* Mini mode: bottom right */
    :host([mode='mini']) {
      bottom: 16px;
      right: 16px;
    }

    /* Expanded mode: bottom right, larger */
    :host([mode='expanded']) {
      bottom: 16px;
      right: 16px;
    }

    /* Full mode: covers viewport */
    :host([mode='full']) {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }

    .widget {
      background: #1e293b;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      animation: slideUp 0.2s ease-out;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .widget.mini {
      width: 360px;
      height: 480px;
    }

    .widget.expanded {
      width: 600px;
      height: 700px;
    }

    .widget.full {
      width: 100vw;
      height: 100vh;
      border-radius: 0;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(0, 0, 0, 0.2);
    }

    .header-title {
      font-size: 14px;
      font-weight: 600;
      color: white;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .header-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      background: transparent;
      border-radius: 6px;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.6);
      transition: all 0.15s ease;
    }

    .header-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    .header-btn svg {
      width: 16px;
      height: 16px;
    }

    .content {
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    /* Conversation list */
    .conversation-list {
      flex: 1;
      overflow-y: auto;
    }

    .conversation-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .conversation-item:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .conversation-item.unread {
      background: rgba(59, 130, 246, 0.1);
    }

    .conversation-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 600;
      color: white;
      flex-shrink: 0;
    }

    .conversation-avatar img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }

    .conversation-info {
      flex: 1;
      min-width: 0;
    }

    .conversation-name {
      font-size: 13px;
      font-weight: 500;
      color: white;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .conversation-preview {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 2px;
    }

    .conversation-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
    }

    .conversation-time {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.4);
    }

    .conversation-badge {
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      background: #3b82f6;
      border-radius: 9px;
      font-size: 10px;
      font-weight: 600;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* Empty state */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 24px;
      text-align: center;
    }

    .empty-icon {
      width: 48px;
      height: 48px;
      color: rgba(255, 255, 255, 0.3);
      margin-bottom: 16px;
    }

    .empty-title {
      font-size: 14px;
      font-weight: 500;
      color: white;
      margin-bottom: 8px;
    }

    .empty-text {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.5);
    }

    /* Loading state */
    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top-color: #3b82f6;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* Search */
    .search-bar {
      padding: 12px 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .search-input {
      width: 100%;
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 6px;
      color: white;
      font-size: 13px;
      outline: none;
      transition: all 0.15s ease;
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .search-input:focus {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(59, 130, 246, 0.5);
    }

    /* New conversation button */
    .new-conversation-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      width: calc(100% - 32px);
      margin: 12px 16px;
      padding: 10px 12px;
      background: #3b82f6;
      border: none;
      border-radius: 6px;
      color: white;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .new-conversation-btn:hover {
      background: #2563eb;
    }

    .new-conversation-btn svg {
      width: 16px;
      height: 16px;
    }

    /* Connecting state */
    .connecting-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      gap: 12px;
      color: rgba(255, 255, 255, 0.6);
      font-size: 13px;
    }

    .start-conversation-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-top: 12px;
      padding: 10px 20px;
      background: #3b82f6;
      border: none;
      border-radius: 8px;
      color: white;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .start-conversation-btn:hover {
      background: #2563eb;
    }

    .start-conversation-btn svg {
      width: 16px;
      height: 16px;
    }

    /* Responsive */
    @media (max-width: 640px) {
      :host([mode='mini']),
      :host([mode='expanded']) {
        top: 48px;
        left: 0;
        right: 0;
        bottom: 0;
      }

      .widget.mini,
      .widget.expanded {
        width: 100%;
        height: 100%;
        border-radius: 0;
        border: none;
      }

      .conversation-item {
        min-height: 56px;
      }
    }

    @media (max-width: 400px) {
      .widget.mini {
        width: calc(100vw - 32px);
        height: calc(100vh - 80px);
        bottom: 8px;
        right: 8px;
      }
    }
  `;

  @property({ type: String }) token = '';
  @property({ type: String }) messengerUrl = '';
  @property({ type: String }) userId = '';
  @property({ type: String, reflect: true }) mode: MessengerWidgetMode = 'mini';

  @state() private conversations: Conversation[] = [];
  @state() private activeConversation: Conversation | null = null;
  @state() private loading = true;
  @state() private searchQuery = '';

  private unsubscribers: (() => void)[] = [];

  connectedCallback() {
    super.connectedCallback();
    this.loadConversations();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
  }

  private setupEventListeners() {
    // Listen for new messages to update conversation list
    this.unsubscribers.push(
      eventBus.on(Events.MESSENGER_MESSAGE_RECEIVED, (message: unknown) => {
        const msg = message as Message;
        this.updateConversationWithMessage(msg);
      })
    );

    // Listen for conversation updates
    this.unsubscribers.push(
      eventBus.on('messenger:conversation:updated', (conversation: unknown) => {
        this.updateConversation(conversation as Conversation);
      })
    );
  }

  private async loadConversations() {
    this.loading = true;
    try {
      const response = await messengerAPI.getConversations({ limit: 20 });
      this.conversations = Array.isArray(response?.conversations) ? response.conversations : [];
    } catch (e) {
      console.error('[MessengerWidget] Failed to load conversations:', e);
    } finally {
      this.loading = false;
    }
  }

  private updateConversationWithMessage(message: Message) {
    const index = this.conversations.findIndex((c) => c.id === message.conversationId);
    if (index >= 0) {
      const conversation = { ...this.conversations[index] };
      conversation.lastMessage = message;
      conversation.unreadCount++;
      conversation.updatedAt = message.createdAt;

      // Move to top and update
      const newConversations = [
        conversation,
        ...this.conversations.slice(0, index),
        ...this.conversations.slice(index + 1),
      ];
      this.conversations = newConversations;
    }
  }

  private updateConversation(conversation: Conversation) {
    const index = this.conversations.findIndex((c) => c.id === conversation.id);
    if (index >= 0) {
      this.conversations = [
        ...this.conversations.slice(0, index),
        conversation,
        ...this.conversations.slice(index + 1),
      ];
    } else {
      this.conversations = [conversation, ...this.conversations];
    }
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent('moya-messenger-close', {
        bubbles: true,
        composed: true,
      })
    );
    eventBus.emit(Events.MESSENGER_WIDGET_CLOSED, {});
  }

  private handleExpand() {
    this.mode = this.mode === 'mini' ? 'expanded' : this.mode === 'expanded' ? 'full' : 'mini';
  }

  private handleConversationClick(conversation: Conversation) {
    this.activeConversation = conversation;
    // Mark as read
    if (conversation.unreadCount > 0) {
      messengerAPI.markAsRead(conversation.id).catch((e) => {
        console.error('[MessengerWidget] Failed to mark as read:', e);
      });
      // Update local state
      const index = this.conversations.findIndex((c) => c.id === conversation.id);
      if (index >= 0) {
        const updated = { ...this.conversations[index], unreadCount: 0 };
        this.conversations = [
          ...this.conversations.slice(0, index),
          updated,
          ...this.conversations.slice(index + 1),
        ];
      }
    }
  }

  private handleBackToList() {
    this.activeConversation = null;
  }

  private getAvatarColor(name: string): string {
    const colors = [
      'linear-gradient(135deg, #8b5cf6, #ec4899)',
      'linear-gradient(135deg, #3b82f6, #06b6d4)',
      'linear-gradient(135deg, #10b981, #3b82f6)',
      'linear-gradient(135deg, #f59e0b, #ef4444)',
      'linear-gradient(135deg, #ec4899, #8b5cf6)',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  }

  private getConversationName(conversation: Conversation): string {
    if (conversation.name) return conversation.name;
    if (conversation.type === 'direct' && conversation.participants.length > 0) {
      return conversation.participants[0].name;
    }
    return 'Conversation';
  }

  private getConversationInitials(conversation: Conversation): string {
    const name = this.getConversationName(conversation);
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  private formatTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  }

  private renderConversationList() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="spinner"></div>
        </div>
      `;
    }

    if (this.conversations.length === 0) {
      return html`
        <div class="empty-state">
          <svg class="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div class="empty-title">No conversations yet</div>
          <div class="empty-text">Start a new conversation to begin messaging</div>
          <button class="start-conversation-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" stroke-linecap="round"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke-linecap="round"/>
            </svg>
            Start a conversation
          </button>
        </div>
      `;
    }

    const filteredConversations = this.searchQuery
      ? this.conversations.filter((c) =>
          this.getConversationName(c).toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      : this.conversations;

    return html`
      ${filteredConversations.map((conversation) => {
        const name = this.getConversationName(conversation);
        const initials = this.getConversationInitials(conversation);
        const avatarUrl = conversation.avatarUrl || conversation.participants[0]?.avatarUrl;
        const avatarColor = this.getAvatarColor(name);

        return html`
          <div
            class="conversation-item ${conversation.unreadCount > 0 ? 'unread' : ''}"
            @click="${() => this.handleConversationClick(conversation)}"
          >
            <div class="conversation-avatar" style="background: ${avatarColor}">
              ${avatarUrl
                ? html`<img src="${avatarUrl}" alt="${name}" />`
                : initials}
            </div>
            <div class="conversation-info">
              <div class="conversation-name">${name}</div>
              <div class="conversation-preview">
                ${conversation.lastMessage?.content || 'No messages yet'}
              </div>
            </div>
            <div class="conversation-meta">
              ${conversation.lastMessage
                ? html`<span class="conversation-time">${this.formatTime(conversation.lastMessage.createdAt)}</span>`
                : ''}
              ${conversation.unreadCount > 0
                ? html`<span class="conversation-badge">${conversation.unreadCount}</span>`
                : ''}
            </div>
          </div>
        `;
      })}
    `;
  }

  render() {
    const widgetClasses = classMap({
      widget: true,
      [this.mode]: true,
    });

    return html`
      <div class="${widgetClasses}">
        <div class="header">
          ${this.activeConversation
            ? html`
                <button class="header-btn" @click="${this.handleBackToList}" aria-label="Back to list">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
                <span class="header-title">${this.getConversationName(this.activeConversation)}</span>
              `
            : html`<span class="header-title">Messages</span>`}
          <div class="header-actions">
            <button class="header-btn" @click="${this.handleExpand}" aria-label="Expand">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                ${this.mode === 'mini'
                  ? html`<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke-linecap="round" stroke-linejoin="round" />`
                  : html`<path d="M4 14h6v6M14 4h6v6M20 10l-6 6M4 14l6-6" stroke-linecap="round" stroke-linejoin="round" />`}
              </svg>
            </button>
            <button class="header-btn" @click="${this.handleClose}" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <div class="content">
          ${this.activeConversation
            ? html`
                <moya-messenger-chat
                  .conversation="${this.activeConversation}"
                  .token="${this.token}"
                  .userId="${this.userId}"
                  mode="${this.mode}"
                ></moya-messenger-chat>
              `
            : html`
                ${this.mode !== 'mini'
                  ? html`
                      <div class="search-bar">
                        <input
                          type="text"
                          class="search-input"
                          placeholder="Search conversations..."
                          .value="${this.searchQuery}"
                          @input="${(e: Event) => (this.searchQuery = (e.target as HTMLInputElement).value)}"
                        />
                      </div>
                    `
                  : ''}
                <div class="conversation-list">
                  ${this.renderConversationList()}
                </div>
              `}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-messenger-widget': MoyaMessengerWidget;
  }
}
